import React, { useEffect, useState } from 'react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Button } from '../components/Button';
import { Card, CardHeader, CardContent } from '../components/Card';
import { Badge } from '../components/Badge';
import { BackgroundSlider } from '../components/BackgroundSlider';
import { ArrowRight, Check, User, Brain, MessageSquare, Target, Clock, AlertTriangle, Shield, Zap } from 'lucide-react';
interface DrillingPotPageProps {
  onNavigate?: (path: string) => void;
}
// Declare Paystack on window object
declare global {
  interface Window {
    PaystackPop: any;
  }
}
export function DrillingPotPage({
  onNavigate
}: DrillingPotPageProps) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [paymentOption, setPaymentOption] = useState<'annual' | 'quarterly' | 'split'>('annual');
  const [showForm, setShowForm] = useState(false);
  // Handle scroll to payment if hash is present
  useEffect(() => {
    if (window.location.hash === '#payment') {
      setTimeout(() => {
        const element = document.getElementById('payment');
        if (element) {
          element.scrollIntoView({
            behavior: 'smooth'
          });
        }
      }, 100);
    }
  }, []);
  const handlePayment = () => {
    if (!email || !name) {
      alert('Please enter your name and email to proceed with application');
      setShowForm(true);
      return;
    }
    // Calculate amount based on payment option
    let amount: number;
    let paymentDescription: string;
    if (paymentOption === 'annual') {
      amount = 70000000; // ₦700,000 in kobo
      paymentDescription = 'Annual Commitment (Full Payment)';
    } else if (paymentOption === 'quarterly') {
      amount = 35000000; // ₦350,000 in kobo
      paymentDescription = 'Quarterly Commitment (First Payment)';
    } else {
      amount = 35000000; // ₦350,000 in kobo (50% upfront)
      paymentDescription = 'Split Payment (50% Upfront - Balance due Month 2)';
    }
    // Paystack integration
    const handler = window.PaystackPop.setup({
      key: 'pk_live_c2f71547b29d95243a012d9ef2f8e03485d942e7',
      email: email,
      amount: amount,
      currency: 'NGN',
      ref: 'DP_' + Math.floor(Math.random() * 1000000000 + 1),
      metadata: {
        custom_fields: [{
          display_name: 'Full Name',
          variable_name: 'full_name',
          value: name
        }, {
          display_name: 'Program',
          variable_name: 'program',
          value: 'The Drilling Pot (One-on-One)'
        }, {
          display_name: 'Payment Option',
          variable_name: 'payment_option',
          value: paymentOption
        }, {
          display_name: 'Payment Description',
          variable_name: 'payment_description',
          value: paymentDescription
        }]
      },
      callback: function (response: any) {
        // Payment successful - redirect to WhatsApp community
        if (paymentOption === 'split') {
          alert('First payment received! You will be contacted about the second payment (₦350,000) at the beginning of month 2. You will now be redirected to schedule your alignment conversation.');
        } else {
          alert('Application fee received! You will now be redirected to schedule your alignment conversation.');
        }
        window.location.href = 'https://chat.whatsapp.com/CPhnSFkreyVJl4M8AF8zZo';
      },
      onClose: function () {
        // Payment cancelled
        console.log('Payment cancelled');
      }
    });
    handler.openIframe();
  };
  const scrollToPayment = () => {
    const element = document.getElementById('payment');
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth'
      });
    } else {
      setShowForm(true);
    }
  };
  const scrollToDetails = () => {
    const element = document.getElementById('overview');
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  return <div className="min-h-screen bg-[#f5f5f7] text-[#1f2933] font-sans selection:bg-salon-red/20 selection:text-[#1f2933]" data-id="element-869">
      {/* Load Paystack Script */}
      <script src="https://js.paystack.co/v1/inline.js" data-id="element-870"></script>

      <Navbar onNavigate={onNavigate} data-id="element-871" />

      {/* Background Slider */}
      <BackgroundSlider data-id="element-872" />

      {/* HERO SECTION */}
      <section className="relative border-b border-dashed border-[#9ca3af] py-24 sm:py-32 overflow-hidden" data-id="element-873">
        <div className="absolute inset-0 z-10 bg-[linear-gradient(to_right,#9ca3af20_1px,transparent_1px),linear-gradient(to_bottom,#9ca3af20_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)]" data-id="element-874" />

        <div className="relative z-20 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center" data-id="element-875">
          <Badge variant="secondary" className="mb-8 mx-auto font-mono text-xs tracking-wider border-salon-red text-salon-red bg-salon-red/10" data-id="element-876">
            PRIVATE_APPRENTICESHIP
          </Badge>

          <h1 className="mx-auto max-w-5xl text-5xl font-bold tracking-tight sm:text-7xl mb-4 text-[#1f2933]" data-id="element-877">
            THE DRILLING POT
          </h1>

          <p className="font-mono text-sm text-[#9ca3af] mb-6 tracking-widest uppercase" data-id="element-878">
            A 12-Month Private Apprenticeship
          </p>

          <p className="mx-auto max-w-2xl text-lg font-mono text-[#1f2933]/80 mb-10 leading-relaxed" data-id="element-879">
            In Personal Brand, AI Leverage & Public Presence.
            <br data-id="element-880" />
            <span className="text-salon-red font-bold" data-id="element-881">
              A practice-driven learning relationship built around real output,
              real feedback, and real-world execution.
            </span>
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-882">
            <Button size="large" rightIcon={<ArrowRight className="w-4 h-4" data-id="element-884" />} onClick={scrollToPayment} className="bg-salon-red hover:bg-salon-red/90 border-salon-red text-white" data-id="element-883">
              APPLY_NOW
            </Button>
            <Button variant="secondary" size="large" onClick={scrollToDetails} className="border-[#1f2933] text-[#1f2933] hover:bg-[#1f2933] hover:text-white" data-id="element-885">
              LEARN_MORE
            </Button>
          </div>
        </div>
      </section>

      {/* OVERVIEW */}
      <section id="overview" className="py-24 border-b border-dashed border-[#9ca3af]" data-id="element-886">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8" data-id="element-887">
          <div className="mb-12" data-id="element-888">
            <h2 className="font-mono text-xs font-bold text-salon-red mb-2" data-id="element-889">
              01_OVERVIEW
            </h2>
            <h3 className="text-3xl font-bold text-[#1f2933] mb-6" data-id="element-890">
              Why "The Drilling Pot"?
            </h3>
            <p className="text-lg text-[#1f2933]/80 mb-6 leading-relaxed" data-id="element-891">
              A drilling pot is where pressure, heat, and time work together to
              transform raw material into something useful and durable. This
              program is designed the same way.
            </p>
            <div className="bg-white border-l-4 border-salon-red p-6 mb-8" data-id="element-892">
              <p className="font-mono text-sm text-[#1f2933]" data-id="element-893">
                This is NOT a course.
                <br data-id="element-894" />
                It is NOT motivational coaching.
                <br data-id="element-895" />
                It is NOT recycled theory from YouTube.
              </p>
            </div>
            <p className="text-lg text-[#1f2933]/80 leading-relaxed" data-id="element-896">
              The Drilling Pot is a practice-driven learning relationship built
              around real output, real feedback, and real-world
              execution—without the drag that comes with group programs.
            </p>
          </div>
        </div>
      </section>

      {/* IDEAL PARTICIPANT */}
      <section className="py-24 border-b border-dashed border-[#9ca3af] bg-[#e5e5e7]" data-id="element-897">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8" data-id="element-898">
          <div className="flex flex-col md:flex-row gap-16 items-start" data-id="element-899">
            <div className="w-full md:w-1/2" data-id="element-900">
              <h2 className="font-mono text-xs font-bold text-salon-teal mb-2" data-id="element-901">
                02_IDEAL_PARTICIPANT
              </h2>
              <h3 className="text-3xl font-bold text-[#1f2933] mb-6" data-id="element-902">
                Who This Is For
              </h3>
              <p className="font-mono text-sm text-[#1f2933]/70 mb-8" data-id="element-903">
                This program assumes desire, discipline, and honesty—not prior
                expertise.
              </p>

              <ul className="space-y-4" data-id="element-904">
                {['Consider yourself a beginner but serious about mastery', 'Want to build a personal brand with substance, not aesthetics', 'Want to use AI as a thinking partner, not a shortcut', 'Value lived practice over consuming information', 'Willing to ship imperfect work, receive feedback, and iterate', 'Want faster personal velocity than group environments allow'].map((item, i) => <li key={i} className="flex items-start gap-3 border-b border-dashed border-[#9ca3af]/30 pb-2" data-id="element-905">
                    <Check className="w-5 h-5 text-salon-red flex-shrink-0 mt-0.5" data-id="element-906" />
                    <span className="text-[#1f2933] font-medium" data-id="element-907">{item}</span>
                  </li>)}
              </ul>
            </div>

            <div className="w-full md:w-1/2" data-id="element-908">
              <Card className="bg-[#f5f5f7] border-2 border-[#1f2933]" data-id="element-909">
                <CardHeader title="PROGRAM_PHILOSOPHY" className="font-mono text-xs font-bold text-[#1f2933]" data-id="element-910" />
                <CardContent data-id="element-911">
                  <h4 className="text-xl font-bold mb-4" data-id="element-912">
                    Self-Directed Learning (Heutagogy)
                  </h4>
                  <ul className="space-y-3 mb-6 font-mono text-sm text-[#1f2933]/80" data-id="element-913">
                    <li className="flex gap-2" data-id="element-914">
                      <span className="text-salon-red" data-id="element-915">→</span> You own your
                      goals
                    </li>
                    <li className="flex gap-2" data-id="element-916">
                      <span className="text-salon-red" data-id="element-917">→</span> Learning is
                      driven by action, not lectures
                    </li>
                    <li className="flex gap-2" data-id="element-918">
                      <span className="text-salon-red" data-id="element-919">→</span> Progress is
                      measured by output
                    </li>
                    <li className="flex gap-2" data-id="element-920">
                      <span className="text-salon-red" data-id="element-921">→</span> AI accelerates
                      thinking, doesn't replace it
                    </li>
                    <li className="flex gap-2" data-id="element-922">
                      <span className="text-salon-red" data-id="element-923">→</span> Real work
                      happens in public
                    </li>
                  </ul>
                  <div className="border-t border-dashed border-[#9ca3af] pt-4" data-id="element-924">
                    <p className="italic text-[#1f2933]" data-id="element-925">
                      "You are not taught what to think. You are trained to
                      build, test, refine, and lead yourself over time."
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* CURRICULUM AREAS */}
      <section className="py-24 border-b border-dashed border-[#9ca3af]" data-id="element-926">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8" data-id="element-927">
          <div className="mb-16" data-id="element-928">
            <h2 className="font-mono text-xs font-bold text-salon-red mb-2" data-id="element-929">
              04_CURRICULUM_AREAS
            </h2>
            <h3 className="text-3xl font-bold text-[#1f2933]" data-id="element-930">
              Four Integrated Focus Areas
            </h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-931">
            <Card className="border-l-4 border-l-[#1f2933]" data-id="element-932">
              <CardHeader title="1. Personal Brand Foundations" className="font-bold" data-id="element-933" />
              <CardContent data-id="element-934">
                <User className="w-8 h-8 text-[#1f2933] mb-4" data-id="element-935" />
                <ul className="space-y-2 font-mono text-sm text-[#1f2933]/80" data-id="element-936">
                  <li data-id="element-937">
                    • Clarifying your voice, perspective, and credibility lane
                  </li>
                  <li data-id="element-938">• Translating lived experience into public insight</li>
                  <li data-id="element-939">• Positioning without pretending or posturing</li>
                  <li data-id="element-940">• Building identity before visibility</li>
                </ul>
              </CardContent>
            </Card>

            <Card className="border-l-4 border-l-salon-red" data-id="element-941">
              <CardHeader title="2. LinkedIn & TikTok as Practice Arenas" className="font-bold" data-id="element-942" />
              <CardContent data-id="element-943">
                <MessageSquare className="w-8 h-8 text-salon-red mb-4" data-id="element-944" />
                <ul className="space-y-2 font-mono text-sm text-[#1f2933]/80" data-id="element-945">
                  <li data-id="element-946">• Writing to think, not to perform</li>
                  <li data-id="element-947">• Developing consistency without burnout</li>
                  <li data-id="element-948">• Learning platforms through use, not study</li>
                  <li data-id="element-949">• Building presence, confidence, and audience trust</li>
                </ul>
              </CardContent>
            </Card>

            <Card className="border-l-4 border-l-salon-teal" data-id="element-950">
              <CardHeader title="3. Practical AI Leverage" className="font-bold" data-id="element-951" />
              <CardContent data-id="element-952">
                <Brain className="w-8 h-8 text-salon-teal mb-4" data-id="element-953" />
                <ul className="space-y-2 font-mono text-sm text-[#1f2933]/80" data-id="element-954">
                  <li data-id="element-955">• Using AI to clarify thinking and structure ideas</li>
                  <li data-id="element-956">• Building repeatable workflows for content</li>
                  <li data-id="element-957">• Turning notes and experiences into assets</li>
                  <li data-id="element-958">• Ethical and authentic use of AI in public work</li>
                </ul>
              </CardContent>
            </Card>

            <Card className="border-l-4 border-l-salon-yellow" data-id="element-959">
              <CardHeader title="4. Authority, Teaching & Public Presence" className="font-bold" data-id="element-960" />
              <CardContent data-id="element-961">
                <Target className="w-8 h-8 text-salon-yellow mb-4" data-id="element-962" />
                <ul className="space-y-2 font-mono text-sm text-[#1f2933]/80" data-id="element-963">
                  <li data-id="element-964">• Developing a signature idea or point of view</li>
                  <li data-id="element-965">• Learning to speak, teach, and explain clearly</li>
                  <li data-id="element-966">• Moving from posting content to holding attention</li>
                  <li data-id="element-967">
                    • Preparing for speaking or thought-leadership opportunities
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* STRUCTURE & EXPECTATIONS */}
      <section className="py-24 border-b border-dashed border-[#9ca3af] bg-[#1f2933] text-[#f5f5f7]" data-id="element-968">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8" data-id="element-969">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-16" data-id="element-970">
            <div data-id="element-971">
              <h2 className="font-mono text-xs font-bold text-salon-red mb-2" data-id="element-972">
                05_STRUCTURE
              </h2>
              <h3 className="text-3xl font-bold mb-6" data-id="element-973">
                The Working Relationship
              </h3>
              <ul className="space-y-4" data-id="element-974">
                <li className="flex items-start gap-3" data-id="element-975">
                  <Clock className="w-5 h-5 text-salon-red flex-shrink-0 mt-1" data-id="element-976" />
                  <div data-id="element-977">
                    <strong className="block mb-1" data-id="element-978">
                      Twice-Weekly Live Sessions
                    </strong>
                    <span className="text-[#9ca3af] text-sm font-mono" data-id="element-979">
                      30-minute one-on-one calls (scheduled)
                    </span>
                  </div>
                </li>
                <li className="flex items-start gap-3" data-id="element-980">
                  <Zap className="w-5 h-5 text-salon-red flex-shrink-0 mt-1" data-id="element-981" />
                  <div data-id="element-982">
                    <strong className="block mb-1" data-id="element-983">
                      Direct Thinking Support
                    </strong>
                    <span className="text-[#9ca3af] text-sm font-mono" data-id="element-984">
                      Not generic advice. Continuous guidance and recalibration.
                    </span>
                  </div>
                </li>
                <li className="flex items-start gap-3" data-id="element-985">
                  <Target className="w-5 h-5 text-salon-red flex-shrink-0 mt-1" data-id="element-986" />
                  <div data-id="element-987">
                    <strong className="block mb-1" data-id="element-988">
                      Practical Assignments
                    </strong>
                    <span className="text-[#9ca3af] text-sm font-mono" data-id="element-989">
                      Tied to real public output. Review of published work.
                    </span>
                  </div>
                </li>
              </ul>
              <p className="mt-8 font-mono text-sm text-salon-red border-l-2 border-salon-red pl-4" data-id="element-990">
                This is a working relationship, not passive mentorship.
              </p>
            </div>

            <div data-id="element-991">
              <h2 className="font-mono text-xs font-bold text-salon-yellow mb-2" data-id="element-992">
                06_EXPECTATIONS
              </h2>
              <h3 className="text-3xl font-bold mb-6" data-id="element-993">Realistic Commitment</h3>
              <div className="bg-[#f5f5f7]/10 p-6 border border-dashed border-[#9ca3af]/50 mb-6" data-id="element-994">
                <ul className="space-y-3 text-sm font-mono" data-id="element-995">
                  <li data-id="element-996">• Consistent weekly effort is required</li>
                  <li data-id="element-997">
                    • You will be expected to publish, reflect, and iterate
                  </li>
                  <li data-id="element-998">• Progress depends on action, not attendance</li>
                  <li data-id="element-999">• Designed to run alongside work—not replace it</li>
                </ul>
              </div>
              <div className="flex items-start gap-3 text-salon-yellow" data-id="element-1000">
                <AlertTriangle className="w-5 h-5 flex-shrink-0" data-id="element-1001" />
                <p className="text-sm font-bold" data-id="element-1002">
                  The Drilling Pot rewards commitment. It does not accommodate
                  prolonged inactivity.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CLARIFICATIONS */}
      <section className="py-24 border-b border-dashed border-[#9ca3af]" data-id="element-1003">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center" data-id="element-1004">
          <h2 className="font-mono text-xs font-bold text-[#9ca3af] mb-8" data-id="element-1005">
            07_CLARIFICATIONS
          </h2>
          <h3 className="text-3xl font-bold text-[#1f2933] mb-8" data-id="element-1006">
            To Be Clear
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 text-left" data-id="element-1007">
            <div className="bg-red-50 p-6 border border-red-100" data-id="element-1008">
              <h4 className="font-bold text-red-800 mb-4" data-id="element-1009">What This Is NOT:</h4>
              <ul className="space-y-2 text-sm text-red-700 font-mono" data-id="element-1010">
                <li data-id="element-1011">× A certification program</li>
                <li data-id="element-1012">× A content factory</li>
                <li data-id="element-1013">× A motivational space</li>
                <li data-id="element-1014">× A shortcut to virality</li>
                <li data-id="element-1015">× A guarantee of followers or fame</li>
              </ul>
            </div>
            <div className="bg-green-50 p-6 border border-green-100" data-id="element-1016">
              <h4 className="font-bold text-green-800 mb-4" data-id="element-1017">What This IS:</h4>
              <ul className="space-y-2 text-sm text-green-700 font-mono" data-id="element-1018">
                <li data-id="element-1019">✓ Clarity</li>
                <li data-id="element-1020">✓ Structure</li>
                <li data-id="element-1021">✓ Pressure</li>
                <li data-id="element-1022">✓ Acceleration</li>
                <li data-id="element-1023">✓ Real-world execution</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* INVESTMENT SECTION */}
      <section id="payment" className="py-24 border-b border-dashed border-[#9ca3af] scroll-mt-24" data-id="element-1024">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8" data-id="element-1025">
          <div className="bg-white border-2 border-dashed border-[#1f2933] p-8 md:p-12 text-center relative" data-id="element-1026">
            <h2 className="font-mono text-xs font-bold text-salon-red mb-2" data-id="element-1027">
              08_FINANCIAL_COMMITMENT
            </h2>
            <h3 className="text-3xl font-bold text-[#1f2933] mb-8" data-id="element-1028">
              Your Investment
            </h3>

            {/* Pricing Options */}
            <div className="grid md:grid-cols-3 gap-6 mb-10" data-id="element-1029">
              <div className={`border-2 p-6 cursor-pointer transition-all ${paymentOption === 'annual' ? 'border-salon-red bg-salon-red/5' : 'border-[#9ca3af] hover:border-[#1f2933]'}`} onClick={() => setPaymentOption('annual')} data-id="element-1030">
                <div className="flex justify-between items-start mb-4" data-id="element-1031">
                  <div className="w-5 h-5 rounded-full border-2 border-[#1f2933] flex items-center justify-center" data-id="element-1032">
                    {paymentOption === 'annual' && <div className="w-3 h-3 rounded-full bg-salon-red" data-id="element-1033" />}
                  </div>
                  <Badge variant="success" className="bg-salon-red text-white border-none" data-id="element-1034">
                    PREFERRED
                  </Badge>
                </div>
                <h4 className="text-xl font-bold text-[#1f2933] mb-2" data-id="element-1035">
                  Annual Commitment
                </h4>
                <div className="flex flex-col items-center mb-2" data-id="element-1036">
                  <span className="text-lg text-[#9ca3af] line-through font-mono" data-id="element-1037">
                    ₦1,200,000
                  </span>
                  <span className="text-3xl font-bold text-[#1f2933]" data-id="element-1038">
                    ₦700,000
                  </span>
                </div>
                <p className="text-xs text-[#9ca3af] font-mono" data-id="element-1039">
                  Paid Upfront • Signals Seriousness
                </p>
              </div>

              <div className={`border-2 p-6 cursor-pointer transition-all ${paymentOption === 'split' ? 'border-salon-red bg-salon-red/5' : 'border-[#9ca3af] hover:border-[#1f2933]'}`} onClick={() => setPaymentOption('split')} data-id="element-1040">
                <div className="flex justify-between items-start mb-4" data-id="element-1041">
                  <div className="w-5 h-5 rounded-full border-2 border-[#1f2933] flex items-center justify-center" data-id="element-1042">
                    {paymentOption === 'split' && <div className="w-3 h-3 rounded-full bg-salon-red" data-id="element-1043" />}
                  </div>
                  <Badge variant="success" className="bg-salon-teal text-white border-none" data-id="element-1044">
                    FLEXIBLE
                  </Badge>
                </div>
                <h4 className="text-xl font-bold text-[#1f2933] mb-2" data-id="element-1045">
                  Split Payment
                </h4>
                <div className="flex flex-col items-center mb-2" data-id="element-1046">
                  <span className="text-3xl font-bold text-[#1f2933]" data-id="element-1047">
                    ₦350,000
                  </span>
                  <span className="text-xs text-[#1f2933] font-mono" data-id="element-1048">
                    × 2 payments
                  </span>
                </div>
                <p className="text-xs text-[#9ca3af] font-mono" data-id="element-1049">
                  50% Now • 50% Month 2
                </p>
              </div>

              <div className={`border-2 p-6 cursor-pointer transition-all ${paymentOption === 'quarterly' ? 'border-salon-red bg-salon-red/5' : 'border-[#9ca3af] hover:border-[#1f2933]'}`} onClick={() => setPaymentOption('quarterly')} data-id="element-1050">
                <div className="flex justify-between items-start mb-4" data-id="element-1051">
                  <div className="w-5 h-5 rounded-full border-2 border-[#1f2933] flex items-center justify-center" data-id="element-1052">
                    {paymentOption === 'quarterly' && <div className="w-3 h-3 rounded-full bg-salon-red" data-id="element-1053" />}
                  </div>
                </div>
                <h4 className="text-xl font-bold text-[#1f2933] mb-2" data-id="element-1054">
                  Quarterly Commitment
                </h4>
                <div className="flex flex-col items-center mb-2" data-id="element-1055">
                  <span className="text-3xl font-bold text-[#1f2933]" data-id="element-1056">
                    ₦350,000
                  </span>
                  <span className="text-xs text-[#1f2933] font-mono" data-id="element-1057">
                    per quarter
                  </span>
                </div>
                <p className="text-xs text-[#9ca3af] font-mono" data-id="element-1058">
                  Total: ₦1,400,000 • 4 Payments
                </p>
              </div>
            </div>

            <div className="bg-[#f5f5f7] p-6 mb-8 text-left text-sm font-mono text-[#1f2933]/80" data-id="element-1059">
              <p className="mb-2 font-bold" data-id="element-1060">Payment confirms:</p>
              <ul className="space-y-1 mb-4" data-id="element-1061">
                <li data-id="element-1062">• Commitment to the full year</li>
                <li data-id="element-1063">• Availability to do the work</li>
                <li data-id="element-1064">• Understanding that this is a high-trust engagement</li>
              </ul>
              {paymentOption === 'split' && <div className="mt-4 pt-4 border-t border-dashed border-[#9ca3af]" data-id="element-1065">
                  <p className="text-salon-red font-bold mb-2" data-id="element-1066">
                    Split Payment Schedule:
                  </p>
                  <ul className="space-y-1" data-id="element-1067">
                    <li data-id="element-1068">• First payment: ₦350,000 (today)</li>
                    <li data-id="element-1069">• Second payment: ₦350,000 (beginning of month 2)</li>
                    <li data-id="element-1070">
                      • You will be contacted with payment details for the
                      second installment
                    </li>
                  </ul>
                </div>}
              <p className="text-xs text-[#9ca3af] mt-4" data-id="element-1071">
                Fees cover time, judgment, experience, and sustained access—not
                content volume.
              </p>
            </div>

            {/* Payment Form */}
            {showForm && <div className="mb-6 space-y-4 max-w-md mx-auto text-left" data-id="element-1072">
                <div data-id="element-1073">
                  <label className="block text-xs font-mono text-[#9ca3af] mb-1" data-id="element-1074">
                    FULL_NAME
                  </label>
                  <input type="text" placeholder="Enter your full name" value={name} onChange={e => setName(e.target.value)} className="w-full border-2 border-[#9ca3af] px-4 py-3 font-mono text-sm focus:border-salon-red focus:outline-none" required data-id="element-1075" />
                </div>
                <div data-id="element-1076">
                  <label className="block text-xs font-mono text-[#9ca3af] mb-1" data-id="element-1077">
                    EMAIL_ADDRESS
                  </label>
                  <input type="email" placeholder="Enter your email address" value={email} onChange={e => setEmail(e.target.value)} className="w-full border-2 border-[#9ca3af] px-4 py-3 font-mono text-sm focus:border-salon-red focus:outline-none" required data-id="element-1078" />
                </div>
              </div>}

            <Button size="large" onClick={handlePayment} className="w-full max-w-md bg-salon-red hover:bg-salon-red/90 border-salon-red text-white" rightIcon={<ArrowRight className="w-5 h-5" data-id="element-1080" />} data-id="element-1079">
              SUBMIT_APPLICATION
            </Button>

            <p className="font-mono text-xs text-[#9ca3af] mt-6" data-id="element-1081">
              Secure payment via Paystack • Application fee is non-refundable
            </p>
          </div>
        </div>
      </section>

      {/* ENTRY PROCESS */}
      <section className="py-24 border-b border-dashed border-[#9ca3af]" data-id="element-1082">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 text-center" data-id="element-1083">
          <h2 className="font-mono text-xs font-bold text-[#1f2933] mb-2" data-id="element-1084">
            09_APPLICATION
          </h2>
          <h3 className="text-2xl font-bold text-[#1f2933] mb-6" data-id="element-1085">
            By Application Only
          </h3>
          <div className="flex flex-col md:flex-row items-center justify-center gap-4 text-sm font-mono text-[#1f2933]/80" data-id="element-1086">
            <div className="flex items-center gap-2" data-id="element-1087">
              <span className="w-6 h-6 rounded-full bg-[#1f2933] text-white flex items-center justify-center text-xs" data-id="element-1088">
                1
              </span>
              <span data-id="element-1089">Alignment Conversation</span>
            </div>
            <div className="hidden md:block w-8 h-px bg-[#9ca3af]" data-id="element-1090"></div>
            <div className="flex items-center gap-2" data-id="element-1091">
              <span className="w-6 h-6 rounded-full bg-[#1f2933] text-white flex items-center justify-center text-xs" data-id="element-1092">
                2
              </span>
              <span data-id="element-1093">Confirm Fit</span>
            </div>
            <div className="hidden md:block w-8 h-px bg-[#9ca3af]" data-id="element-1094"></div>
            <div className="flex items-center gap-2" data-id="element-1095">
              <span className="w-6 h-6 rounded-full bg-[#1f2933] text-white flex items-center justify-center text-xs" data-id="element-1096">
                3
              </span>
              <span data-id="element-1097">Begin Apprenticeship</span>
            </div>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-32 text-center bg-[#1f2933] text-white" data-id="element-1098">
        <div className="mx-auto max-w-4xl px-4" data-id="element-1099">
          <h2 className="text-4xl font-bold mb-8 tracking-tight" data-id="element-1100">
            The Drilling Pot is for builders.
          </h2>

          <div className="flex flex-wrap justify-center gap-6 mb-12 font-mono text-sm text-[#9ca3af]" data-id="element-1101">
            <span data-id="element-1102">THINK CLEARLY</span>
            <span data-id="element-1103">ACT CONSISTENTLY</span>
            <span data-id="element-1104">LEARN IN PUBLIC</span>
            <span data-id="element-1105">GROW DELIBERATELY</span>
          </div>

          <p className="text-lg text-[#f5f5f7]/80 mb-12 italic" data-id="element-1106">
            "If that resonates, the next step is a conversation—not persuasion."
          </p>

          <Button size="large" className="min-w-[250px] bg-salon-red hover:bg-white hover:text-[#1f2933] border-salon-red text-white" onClick={scrollToPayment} data-id="element-1107">
            BEGIN_APPLICATION
          </Button>
        </div>
      </section>

      <Footer onNavigate={onNavigate} data-id="element-1108" />
    </div>;
}